"use client";

import { useContext } from "react";

import classNames from "classnames";
import isEmpty from "lodash/isEmpty";

import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import ProductCard from "@components/swagmagic/dynamic/cards/product";
import HorizontalCarouselWrapper from "@components/swagmagic/dynamic/horizontal-carousel-wrapper";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { MobileScrollWrapper } from "@components/swagmagic/dynamic/mobile-scroll-wrapper";

import {
  PL_01,
  PL_04,
  PL_05,
  PL_10,
  PL_06,
  PL_11,
  PL_12,
  PL_07,
  CUSTOM,
  DEFAULT,
  ProductListingVariant,
  ViewType,
  DynamicSlicesData,
  getProductListingCarouselSlidesPerRow,
  getHeaderButtonLinkDataAndType,
} from "@helpers/plasmic/slices";
import { getCategoryLinkQueryFromFilterOptions } from "@helpers/plasmic/catalog-entries";

import { PRODUCT_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import { getIsLockerShop } from "@selectors/store";

import styles from "./main.module.scss";

export interface ProductListingBaseProps {
  viewType: ViewType;
  categorySlug?: string;
  variant: ProductListingVariant;
  identifier?: string;
  className?: string;
  title?: string;
  description?: string;
  showBadgesAndColorOptions: boolean;
  btnText?: string;
  btnLinkType: string;
  btnLinkOtherUrl?: string;
  btnLinkCategorySlug?: string;
  linkToNewTab: boolean;
  dynamicSlicesData?: DynamicSlicesData;
  filterOptions?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

export interface ProductListingProps extends ProductListingBaseProps {
  products?: any[];
  isProductsLoading?: boolean;
  selectedCategory?: any;
}

const ProductListingCarousel = (props: ProductListingProps) => {
  const {
    dynamicSlicesData,
    className,
    variant,
    title,
    description,
    showBadgesAndColorOptions,
    btnText,
    btnLinkType,
    btnLinkOtherUrl,
    btnLinkCategorySlug,
    linkToNewTab,
    identifier,
    categorySlug,
    viewType,
    products: fetchedProducts,
    isProductsLoading = false,
    selectedCategory,
    filterOptions,
    linkRouteDefaults,
  } = props;

  const { isMobileDevice, isDesktopDevice: isDesktop } = useContext(CurrentDeviceContext);

  const hasMobileScroll = variant === PL_04 || variant === PL_05;
  const hasMdOnlyScroll =
    variant === PL_06 ||
    variant === PL_07 ||
    variant === PL_10 ||
    variant === PL_11 ||
    variant === PL_12;

  // PL_04/PL_05: scroll track on mobile + md; PL_06/PL_07: scroll track on md only, column on mobile
  const isMdOnlyScroll = hasMdOnlyScroll && !isDesktop && !isMobileDevice;
  const showScrollTrack = (hasMobileScroll && !isDesktop) || isMdOnlyScroll;
  const showMobileColumn = hasMdOnlyScroll && !isDesktop && isMobileDevice;

  if (!dynamicSlicesData) {
    return null;
  }

  const { store } = dynamicSlicesData;

  const missingCategorySlug = viewType === DEFAULT && isEmpty(categorySlug);
  const missingIdentifier = viewType === CUSTOM && isEmpty(identifier);
  const missingSelectedCategory = viewType === DEFAULT && !selectedCategory;
  const hasCustomScroll = hasMobileScroll || hasMdOnlyScroll;

  if (
    !viewType ||
    missingIdentifier ||
    missingCategorySlug ||
    missingSelectedCategory ||
    (!isProductsLoading && !fetchedProducts?.length)
  ) {
    return null;
  }

  const defaultSlidesPerRow = getProductListingCarouselSlidesPerRow(variant);
  const isLockerShop = getIsLockerShop({ store });

  const menuIdentifier = viewType === CUSTOM ? identifier : undefined;

  const linkDataAndType = getHeaderButtonLinkDataAndType({
    linkType: btnLinkType,
    otherUrl: btnLinkOtherUrl,
    categorySlug: btnLinkCategorySlug || categorySlug,
    menuIdentifier,
  });

  const renderProductCard = (product: any, withColCard = true) => (
    <LinkWithLang
      {...linkRouteDefaults}
      type={PRODUCT_LINK_TYPE}
      data={product}
      target="_self"
      key={`product-${product.id}`}
      className={withColCard ? styles.colCard : undefined}
    >
      <ProductCard
        key={product?.id}
        product={product}
        isLockerShop={isLockerShop}
        showBadgesAndColorOptions={showBadgesAndColorOptions}
        variant={variant}
      />
    </LinkWithLang>
  );

  let containerClassName = styles.primarySlicesContainer;
  if (hasMobileScroll) containerClassName = styles.mobileScrollSlicesContainer;
  else if (hasMdOnlyScroll) containerClassName = styles.tabletScrollSlicesContainer;

  const showWithCarousel = hasCustomScroll ? !showScrollTrack && !showMobileColumn : undefined;

  const wrapperClassName = classNames(
    styles.horizontalCarouselWrapper,
    className,
    styles.productListCarousel,
    {
      [styles.largeImageCard]: variant === PL_01,
      [styles.borderedProductTile]: variant === PL_01 || variant === PL_04 || variant === PL_05,
    },
  );

  const carouselContainerClassName = classNames({
    [styles.productImageSmall]: variant === PL_04 || variant === PL_05,
    [styles.productImageMedium]:
      variant === PL_01 || variant === PL_06 || variant === PL_11 || variant === PL_12,
    [styles.productImageLarge]: variant === PL_07 || variant === PL_10,
    [styles.productImageSmallTablet]: variant === PL_01,
    [styles.productImageLargeTablet]:
      variant === PL_06 ||
      variant === PL_07 ||
      variant === PL_10 ||
      variant === PL_11 ||
      variant === PL_12,
  });

  const scrollCardClassName = hasMdOnlyScroll
    ? styles.mdScrollCardWrapper
    : styles.pl04MobileCardWrapper;

  const titleContainerClassName = styles.titleNoPaddingRight;
  const headerContainerClassName = hasMobileScroll ? styles.headerPaddingRight : undefined;

  const renderChildren = () => {
    if (showScrollTrack) {
      return (
        <MobileScrollWrapper
          containerClassName={styles.pl04MobileScrollContainer}
          scrollTrackClassName={styles.pl04ScrollTrack}
          scrollThumbClassName={styles.pl04ScrollThumb}
          isLoading={isProductsLoading}
        >
          {fetchedProducts?.map((product) => (
            <div key={`product-${product.id}`} className={scrollCardClassName}>
              {renderProductCard(product, false)}
            </div>
          ))}
        </MobileScrollWrapper>
      );
    }

    if (showMobileColumn) {
      return (
        <div className={styles.mobileColumnContainer}>
          {fetchedProducts?.map((product) => renderProductCard(product, false))}
        </div>
      );
    }

    return fetchedProducts?.map((product) => renderProductCard(product));
  };

  return (
    <div className={`${containerClassName} ${className}`}>
      <HorizontalCarouselWrapper
        key={`${identifier}-${categorySlug}-${viewType}`}
        className={wrapperClassName}
        title={title}
        description={description}
        btnText={btnText}
        linkToNewTab={linkToNewTab}
        slidesPerRow={defaultSlidesPerRow}
        showLoader={isProductsLoading}
        showWithCarousel={showWithCarousel}
        carouselContainerClassName={carouselContainerClassName}
        headerContainerClassName={headerContainerClassName}
        titleContainerClassName={titleContainerClassName}
        linkDataAndType={linkDataAndType}
        btnLinkQueryParams={getCategoryLinkQueryFromFilterOptions(filterOptions)}
        linkRouteDefaults={linkRouteDefaults}
      >
        {renderChildren()}
      </HorizontalCarouselWrapper>
    </div>
  );
};

export default ProductListingCarousel;
